import React from 'react';
import { message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import data from './data';
import './index.css';
import Copyright from '../../components/CopyRight';

const PAGES = ['home', 'about', 'services', 'network', 'tracking', 'contact'];

class Home extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      lang: 'jp',
      page: 'home',
      mobileNav: false,
      trackInput: '',
      trackResult: null,
      trackNotFound: false,
      trackLoading: false,
      metaAnimated: false,
      contactForm: { name: '', company: '', email: '', phone: '', category: '', message: '' },
    };
    this.heroMetaRef = React.createRef();
    this.heroMetaObserver = null;
  }

  t = (cn, jp) => this.state.lang === 'cn' ? cn : jp;

  setLang = (lang) => this.setState({ lang });

  // 解析 '3,000+' → { count: 3000, suffix: '+' }，'20,000㎡+' → { count: 20000, suffix: '㎡+' }
  parseMetaNum = (numStr) => {
    const cleaned = numStr.replace(/,/g, '');
    const match = cleaned.match(/^(\d+)(.*)$/);
    return match ? { count: +match[1], suffix: match[2] } : { count: 0, suffix: numStr };
  };

  // easeOutExpo 数字递增动画
  countUp = (el) => {
    const target = +el.dataset.count;
    const suffix = el.dataset.suffix || '';
    const dur = 12000;
    // 先写入终值测量实际宽高，锁定尺寸防止动画过程中跳动
    el.textContent = target.toLocaleString() + suffix;
    el.style.width = el.offsetWidth + 'px';
    el.style.height = el.offsetHeight + 'px';
    el.textContent = '0';
    let t0 = null;
    const step = (t) => {
      if (!t0) t0 = t;
      const p = Math.min((t - t0) / dur, 1);
      const eased = 1 - Math.pow(2, -10 * p); // easeOutExpo，先快后慢
      el.textContent = Math.round(target * (p === 1 ? 1 : eased)).toLocaleString() + suffix;
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  componentDidMount() {
    const container = this.heroMetaRef.current;
    if (!container) return;
    this.heroMetaObserver = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          container.querySelectorAll('[data-count]').forEach((el) => this.countUp(el));
          this.heroMetaObserver.unobserve(e.target); // 只播一次
          setTimeout(() => this.setState({ metaAnimated: true }), 12100);
        }
      });
    }, { threshold: 0.4 });
    this.heroMetaObserver.observe(container);
  }

  componentWillUnmount() {
    if (this.heroMetaObserver) {
      this.heroMetaObserver.disconnect();
    }
  }

  setPage = (page) => {
    this.setState({ page, mobileNav: false, trackResult: null, trackNotFound: false });
    window.scrollTo(0, 0);
  };

  handleTrack = (e) => {
    e && e.preventDefault();
    const { trackInput } = this.state;
    const trimmed = trackInput.trim();
    if (!trimmed) return;
    this.setState({ page: 'tracking', trackInput: trimmed, trackResult: null, trackNotFound: false, trackLoading: true }, async () => {
      window.scrollTo(0, 0);
      try {
        const ts = Date.now();
        const res = await fetch(`/service/shipment/get?id=${encodeURIComponent(trimmed)}&_=${ts}`);
        const json = await res.json();
        if (json.code === 200 && json.data) {
          const d = json.data;
          const activities = (d.activities || []).map(a => ({
            time: a.recordtime,
            event: a.activitytext,
            loc: '',
          }));
          const result = {
            trackNo: d.id,
            status: activities.length > 0 ? activities[0].event : '—',
            gtype: d.gtype || '—',
            pcs: d.pcs != null ? `${d.pcs} ${d.pcsunit || ''}`.trim() : '—',
            freightPayment: d.freight_payment || '—',
            dutyPayment: d.duty_payment || '—',
            receiptType: d.receipt_type || '—',
            createtime: d.createtime || '—',
            events: activities,
          };
          this.setState({ trackResult: result, trackNotFound: false, trackLoading: false });
        } else {
          this.setState({ trackResult: null, trackNotFound: true, trackLoading: false });
        }
      } catch (err) {
        this.setState({ trackResult: null, trackNotFound: true, trackLoading: false });
      }
    });
  };

  handleContactChange = (field, value) => {
    this.setState({ contactForm: { ...this.state.contactForm, [field]: value } });
  };

  handleContactSubmit = (e) => {
    e.preventDefault();
    message.success(this.t('提交成功，我们会尽快与您联系。', '送信しました。担当者よりご連絡いたします。'));
  };

  importData = () => data[this.state.lang];

  // ---------- Render Helpers ----------

  renderKicker = (text) => text ? <span className="kds-kicker">{text}</span> : null;

  renderSectionHead = (kicker, title, lead, center = false) => (
    <div className={`kds-section-head${center ? ' center' : ''}`}>
      {this.renderKicker(kicker)}
      <h2>{title}</h2>
      {lead && <p className="kds-lead">{lead}</p>}
    </div>
  );

  renderHeader = () => {
    const d = this.importData();
    const { lang, page, mobileNav } = this.state;
    return (
      <header className="kds-header">
        <div className="kds-header-inner">
          <a className="kds-brand" onClick={() => this.setPage('home')}>
            <img src="/logo-mark.png" alt="KDS.,LTD" />
            <div className="kds-brand-text">
              <strong>KDS.,LTD</strong>
            </div>
          </a>
          <nav className={`kds-nav${mobileNav ? ' open' : ''}`}>
            {d.nav.map((item, i) => (
              <a key={i} href={`#${PAGES[i]}`} className={page === PAGES[i] ? 'is-active' : ''} onClick={(e) => { e.preventDefault(); this.setPage(PAGES[i]); }}>{item}</a>
            ))}
          </nav>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div className="kds-lang-switch">
              <a className={lang === 'jp' ? 'is-active' : ''} onClick={() => this.setLang('jp')}>JP</a>
              <a className={lang === 'cn' ? 'is-active' : ''} onClick={() => this.setLang('cn')}>中文</a>
              
              <a href="http://56wl.kds868.com" target="_blank" rel="noopener noreferrer">头程下单系统</a>
              <a href="http://wms.kds868.com" target="_blank" rel="noopener noreferrer">海外仓下单系统</a>
            </div>
            <button className="menu-toggle" aria-label="menu" onClick={() => this.setState({ mobileNav: !mobileNav })}><span /></button>
          </div>
        </div>
      </header>
    );
  };

  renderFooter = () => {
    const d = this.importData();
    const f = d.footer;
    return (
      <footer className="kds-footer">
        <div className="kds-container">
          <div className="kds-footer-grid">
            <div className="kds-footer-brand">
              <div className="kds-brand">
                <img src="/logo-mark.png" alt="KDS" width="42" height="42" />
                <div className="kds-brand-text">
                  <strong>{f.brand.name}</strong>
                </div>
              </div>
              <p>{f.brand.desc}</p>
            </div>
            <div>
              <h5>{f.service.title}</h5>
              <ul>
                {f.service.links.map((l, i) => <li key={i}><a onClick={() => this.setPage('services')}>{l}</a></li>)}
                <li><a href="/auth/login" target="_blank" rel="noopener noreferrer">后台管理</a></li>
              </ul>
            </div>
            <div>
              <h5>{f.company.title}</h5>
              <ul>
                <li><a onClick={() => this.setPage('about')}>{f.company.links[0]}</a></li>
                <li><a onClick={() => this.setPage('network')}>{f.company.links[1]}</a></li>
                <li><a onClick={() => this.setPage('tracking')}>{f.company.links[2]}</a></li>
                <li><a onClick={() => this.setPage('contact')}>{f.company.links[3]}</a></li>
                {f.company.extLinks && f.company.extLinks.map((el, i) => <li key={`ext-${i}`}><a href={el.href} target="_blank" rel="noopener noreferrer">{el.text}</a></li>)}
              </ul>
            </div>
            <div>
              <h5>{f.contact.title}</h5>
              <ul>
                <li>{f.contact.label}</li>
                <li style={{ color: 'rgba(255,255,255,.55)', whiteSpace: 'pre-line' }}>{f.contact.addr}</li>
                {f.contact.phone1 && <li style={{ color: 'rgba(255,255,255,.55)' }}>{this.t('电话：', 'TEL：')}{f.contact.phone1}<br />{this.t('电话：', 'TEL：')}{f.contact.phone2}</li>}
                {f.contact.email && <li style={{ color: 'rgba(255,255,255,.55)' }}>{this.t('邮箱：', 'MAIL：')}{f.contact.email}</li>}
              </ul>
            </div>
          </div>
          <div className="kds-footer-bottom">
            <span>{f.bottom}</span>
            <Copyright />
            <span>All Rights Reserved.</span>
          </div>
        </div>
      </footer>
    );
  };

  renderCTA = (cta) => (
    <section className="kds-cta-banner" id="cta">
      <div className="kds-container kds-cta-inner">
        <div>
          <h2>{cta.title}</h2>
          <p>{cta.desc}</p>
        </div>
        <button className="kds-btn kds-btn-ghost kds-btn-arrow" onClick={() => this.setPage('contact')}>{cta.btn}</button>
      </div>
    </section>
  );

  // ========== HOME PAGE ==========

  renderHero = () => {
    const d = this.importData();
    const h = d.hero;
    const { trackInput } = this.state;
    return (
      <section className="kds-hero" id="home">
        <div className="kds-hero-inner">
          <div>
            <span className="kds-kicker">{h.kicker}</span>
            <h1>{h.title[0]}<br />{h.title[1]}<em>{h.title[2]}</em>{h.title[3]}</h1>
            <p className="kds-lead">{h.lead}</p>
            <div className="kds-hero-actions">
              {h.actions.map((a, i) => (
                <button key={i} className={`kds-btn ${i === 0 ? 'kds-btn-primary' : 'kds-btn-ghost'} kds-btn-arrow`} onClick={() => i === 0 ? this.setPage('services') : this.setPage('contact')}>{a.text}</button>
              ))}
            </div>
            <div className="kds-hero-meta" ref={this.heroMetaRef}>
              {h.meta.map((m, i) => {
                const { count, suffix } = this.parseMetaNum(m.num);
                return (
                  <div className="kds-hero-meta-item" key={i}>
                    <strong data-count={count} data-suffix={suffix}>
                      {this.state.metaAnimated ? count.toLocaleString() + suffix : '0'}
                    </strong>
                    <span>{m.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="kds-hero-track">
            <h3>{h.track.title}</h3>
            <p>{this.t('请输入追踪号', '追跡番号を入力してください')}</p>
            <form onSubmit={e => e.preventDefault()}>
              <input type="text" placeholder={h.track.placeholder} value={trackInput} onChange={e => this.setState({ trackInput: e.target.value })} onKeyDown={e => e.key === 'Enter' && this.handleTrack(e)} />
              <button type="button" onClick={this.handleTrack}>{this.t('追踪', '追跡')}</button>
            </form>
            <p className="kds-hero-track-hint">{h.track.hint}</p>
          </div>
        </div>
      </section>
    );
  };

  renderStrengths = () => {
    const d = this.importData();
    const s = d.strengths;
    return (
      <section className="kds-section kds-strengths" id="strengths">
        <div className="kds-container">
          {this.renderSectionHead(s.kicker, s.title, s.lead)}
          <div className="kds-strength-grid">
            {s.items.map((item, i) => (
              <div className="kds-strength-item" key={i}>
                <span className="num">{item.num}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  renderServicesOverview = () => {
    const d = this.importData();
    const s = d.servicesOverview;
    return (
      <section className="kds-section" id="services">
        <div className="kds-container">
          {this.renderSectionHead(s.kicker, s.title, s.lead)}
          <div className="kds-services-grid">
            {s.cards.map((card, i) => (
              <div className="kds-service-card" key={i} onClick={() => this.setPage('services')}>
                <span className="num">{card.num}</span>
                <span className="en">{card.en}</span>
                <h3>{card.title}</h3>
                <p>{card.desc}</p>
                <span className="more">{this.t('查看详情', '詳細を見る')}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  renderProcess = () => {
    const d = this.importData();
    const p = d.process;
    return (
      <section className="kds-section kds-process" id="process">
        <div className="kds-container">
          <div className="kds-section-head">
            {this.renderKicker(p.kicker)}
            <h2>{p.title}</h2>
            <p className="kds-lead">{p.lead}</p>
          </div>
          <div className="kds-process-tabs">
            <button className="active" type="button">{p.export.label}</button>
          </div>
          <div className="kds-process-flow">
            {p.export.steps.map((step, i) => (
              <div className={`kds-process-step${i === 0 ? ' active' : ''}`} key={i}>
                <div className="dot">{step.num}</div>
                <h4>{step.title}</h4>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="kds-process-tabs" style={{ marginTop: 64 }}>
            <button className="active" type="button">{p.import.label}</button>
          </div>
          <div className="kds-process-flow">
            {p.import.steps.map((step, i) => (
              <div className={`kds-process-step${i === 0 ? ' active' : ''}`} key={i}>
                <div className="dot">{step.num}</div>
                <h4>{step.title}</h4>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  renderValues = () => {
    const d = this.importData();
    const v = d.values;
    return (
      <section className="kds-section" id="values">
        <div className="kds-container">
          {this.renderSectionHead(v.kicker, v.title, v.lead)}
          <div className="kds-values-grid">
            {v.items.map((item, i) => (
              <div className="kds-value-card" key={i}>
                <span className="num">{item.num}</span>
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  renderTimeline = () => {
    const d = this.importData();
    const t = d.timeline;
    return (
      <section className="kds-section kds-section-highlight" id="timeline">
        <div className="kds-container">
          {this.renderSectionHead(t.kicker, t.title, t.lead)}
          <div className="kds-timeline-teaser">
            {t.items.map((item, i) => (
              <div className="kds-tl-item" key={i}>
                <strong>{item.year}</strong>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 48 }}>
            <button className="kds-btn kds-btn-arrow" onClick={() => this.setPage('about')}>{t.link.text}</button>
          </div>
        </div>
      </section>
    );
  };

  renderQualifications = () => {
    const d = this.importData();
    const q = d.qualifications;
    const icons = {
      IATA: <svg viewBox="0 0 60 60" fill="none" stroke="#C8102E" strokeWidth="1.5"><circle cx="30" cy="30" r="22"/><path d="M8 30c5-8 39-8 44 0M8 30c5 8 39 8 44 0M30 8v44M14 14l32 32M46 14L14 46"/></svg>,
      NVOCC: <svg viewBox="0 0 60 60" fill="none" stroke="#C8102E" strokeWidth="1.5"><path d="M8 38h44M14 38l4-12h24l4 12M22 26l2-8h12l2 8M30 18v-6"/><circle cx="18" cy="44" r="3"/><circle cx="42" cy="44" r="3"/></svg>,
      CIFA: <svg viewBox="0 0 60 60" fill="none" stroke="#C8102E" strokeWidth="1.5"><path d="M30 8L12 16v12c0 12 8 22 18 24 10-2 18-12 18-24V16L30 8zM22 30l6 6 12-12"/></svg>,
    };
    return (
      <section className="kds-section" id="qualifications">
        <div className="kds-container">
          {this.renderSectionHead(q.kicker, q.title, q.lead, true)}
          <div className="kds-qual-grid">
            {q.items.map((item, i) => (
              <div className="kds-qual-card" key={i}>
                <div className="kds-qual-icon" aria-hidden="true">
                  {icons[item.name] || null}
                </div>
                <h3>{item.name}</h3>
                <p className="kds-qual-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  renderPartners = () => {
    const d = this.importData();
    const p = d.partners;
    return (
      <section className="kds-partners">
        <div className="kds-container">
          <p className="kds-kicker" style={{ textAlign: 'center', display: 'block', marginBottom: 32 }}>{p.kicker}</p>
          <div className="kds-partners-row">
            {p.names.map((name, i) => {
              if (typeof name === 'string') {
                return <span key={i}>{name}</span>;
              }
              return <span key={i}><a href={name.link} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>{name.text}</a></span>;
            })}
          </div>
        </div>
      </section>
    );
  };

  renderHome = () => {
    const d = this.importData();
    return (
      <>
        {this.renderHero()}
        {this.renderStrengths()}
        {this.renderServicesOverview()}
        {this.renderProcess()}
        {this.renderValues()}
        {this.renderTimeline()}
        {this.renderQualifications()}
        {this.renderPartners()}
        {this.renderCTA(d.cta)}
      </>
    );
  };

  // ========== ABOUT PAGE ==========

  renderAbout = () => {
    const d = this.importData();
    const a = d.about;
    return (
      <>
        <section className="kds-page-hero">
          <div className="kds-container">
            <div className="breadcrumb"><a href="#home" onClick={(e) => { e.preventDefault(); this.setPage('home'); }}>HOME</a> &nbsp;／&nbsp; ABOUT</div>
            <h1>{a.hero.title}</h1>
            <p className="kds-lead">{a.hero.lead}</p>
          </div>
          <span className="kds-page-hero-en">About Us</span>
        </section>

        <section className="kds-section" style={{ background: 'var(--c-bg-soft)', padding: '64px 0' }}>
          <div className="kds-container">
            <div className="kds-stats-grid">
              {a.stats.map((s, i) => (
                <div className="kds-stat" key={i}>
                  <strong>{s.num}<sup>{s.sup}</sup></strong>
                  <span>{s.label}<em>{s.em}</em></span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="kds-section kds-section-highlight" id="about-message">
          <div className="kds-container">
            <div className="kds-two-col">
              <div className="col-left">
                {this.renderKicker(a.message.kicker)}
                <h2>{a.message.title[0]}{a.message.title[1]}</h2>
              </div>
              <div className="col-right">
                {a.message.body.map((p, i) => <p key={i} dangerouslySetInnerHTML={{ __html: p }} />)}
              </div>
            </div>
          </div>
        </section>

        <section className="kds-section" id="about-history">
          <div className="kds-container">
            {this.renderSectionHead(a.history.kicker, a.history.title, a.history.lead)}
            <ul className="kds-timeline-history">
              {a.history.items.map((item, i) => (
                <li key={i} className={item.future ? 'future' : ''}>
                  <span className="year">{item.year}</span>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="kds-section kds-section-highlight" id="about-culture">
          <div className="kds-container">
            {this.renderSectionHead(a.culture.kicker, a.culture.title, null, true)}
            <div className="kds-culture-card">
              <span className="kds-kicker">{a.culture.vision.en}</span>
              <h3>{a.culture.vision.title}</h3>
              <p className="kds-lead">{a.culture.vision.desc}</p>
              <p className="kds-muted" style={{ fontSize: 13, marginTop: 8 }}>{a.culture.vision.sub}</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, maxWidth: 960, margin: '0 auto' }}>
              <div className="kds-culture-card" style={{ margin: 0 }}>
                <span className="kds-kicker">{a.culture.spirit.en}</span>
                <h3>{a.culture.spirit.title}</h3>
                <p className="kds-lead" style={{ whiteSpace: 'pre-line' }}>{a.culture.spirit.desc}</p>
              </div>
              <div className="kds-culture-card" style={{ margin: 0 }}>
                <span className="kds-kicker">{a.culture.core.en}</span>
                <h3>{a.culture.core.title}</h3>
                <p className="kds-lead" style={{ whiteSpace: 'pre-line' }}>{a.culture.core.desc}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="kds-section" id="about-advantages">
          <div className="kds-container">
            {this.renderSectionHead(a.advantages.kicker, a.advantages.title, a.advantages.lead)}
            <div className="kds-advantages-grid">
              {a.advantages.items.map((item, i) => (
                <div className="kds-adv" key={i}>
                  <span className="num">{item.num}</span>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="kds-section kds-section-highlight" id="about-qualifications">
          <div className="kds-container">
            {this.renderSectionHead(a.qualifications.kicker, a.qualifications.title, a.qualifications.lead, true)}
            <div className="kds-qual-grid">
              {a.qualifications.items.map((item, i) => (
                <div className="kds-qual-card" key={i}>
                  <h3>{item.name}</h3>
                  <p className="kds-qual-desc">{item.desc}</p>
                  <p className="kds-muted" style={{ fontSize: 11, marginTop: 6 }}>{item.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="kds-section" id="about-profile">
          <div className="kds-container">
            {this.renderSectionHead(a.profile.kicker, a.profile.title)}
            <table className="kds-spec-table">
              <tbody>
                {a.profile.rows.map((row, i) => (
                  <tr key={i}>
                    <th>{row.th}</th>
                    <td>{row.td}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {this.renderCTA(a.cta)}
      </>
    );
  };

  // ========== SERVICES PAGE ==========

  renderServices = () => {
    const d = this.importData();
    const s = d.services;
    return (
      <>
        <section className="kds-page-hero">
          <div className="kds-container">
            <div className="breadcrumb"><a href="#home" onClick={(e) => { e.preventDefault(); this.setPage('home'); }}>HOME</a> &nbsp;／&nbsp; SERVICES</div>
            <h1>{s.hero.title}</h1>
            <p className="kds-lead">{s.hero.lead}</p>
          </div>
          <span className="kds-page-hero-en">Services</span>
        </section>

        <section className="kds-section">
          <div className="kds-container">
            <div className="kds-transit-bar">
              {s.transit.map((t, i) => (
                <div className="kds-transit-item" key={i}>
                  <span>{t.label}</span>
                  <strong>{t.value}</strong>
                </div>
              ))}
            </div>
          </div>
        </section>

        {s.details.map((detail, i) => (
          <section className="kds-service-detail" key={i} id={detail.id}>
            <div className="kds-container">
              <div className="kds-service-detail-grid">
                <div>
                  <span className="num">{detail.num}</span>
                  <h2>{detail.title}</h2>
                  <span className="en-sub">{detail.en}</span>
                  <p className="kds-lead">{detail.desc}</p>
                  <p className="kds-lead-time">{detail.leadTime}</p>
                </div>
                <div>
                  <ul className="kds-feature-list">
                    {detail.features.map((f, j) => (
                      <li key={j}>
                        <strong>{f.key}</strong>
                        <span>{f.val}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>
        ))}

        <section className="kds-section kds-process" id="service-flow">
          <div className="kds-container">
            <div style={{ textAlign: 'center' }}>
              {this.renderKicker(s.flow.kicker)}
              <h2>{s.flow.title}</h2>
              <p className="kds-lead">{s.flow.lead}</p>
            </div>
            <ul className="kds-flow-10step">
              {s.flow.steps.map((step, i) => (
                <li key={i}>
                  <span className="step-num">{step.num}</span>
                  <h4>{step.title}</h4>
                  <p>{step.desc}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="kds-section" id="service-coverage">
          <div className="kds-container">
            {this.renderSectionHead(s.coverage.kicker, s.coverage.title, s.coverage.lead)}
            <div className="kds-values-grid">
              {s.coverage.items.map((item, i) => (
                <div className="kds-value-card" key={i}>
                  <span className="num">{item.num}</span>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {this.renderCTA(s.cta)}
      </>
    );
  };

  // ========== NETWORK PAGE ==========

  renderNetwork = () => {
    const d = this.importData();
    const n = d.network;
    return (
      <>
        <section className="kds-page-hero">
          <div className="kds-container">
            <div className="breadcrumb"><a href="#home" onClick={(e) => { e.preventDefault(); this.setPage('home'); }}>HOME</a> &nbsp;／&nbsp; NETWORK</div>
            <h1>{n.hero.title}</h1>
            <p className="kds-lead">{n.hero.lead}</p>
          </div>
          <span className="kds-page-hero-en">Network</span>
        </section>

        <section className="kds-section">
          <div className="kds-container">
            {this.renderSectionHead(n.global.kicker, n.global.title)}
          </div>
        </section>

        <section className="kds-section kds-section-highlight">
          <div className="kds-container">
            {this.renderSectionHead(n.chinaOffices.kicker, n.chinaOffices.title, n.chinaOffices.lead)}
            <div className="kds-offices">
              {n.chinaOffices.offices.map((off, i) => (
                <div className="kds-office-card" key={i}>
                  <p className="pin">{off.pin}</p>
                  <h3>{off.title}</h3>
                  <p className="addr">{off.addr}</p>
                  <p className="role">{off.role}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="kds-section">
          <div className="kds-container">
            {this.renderSectionHead(n.japanOffices.kicker, n.japanOffices.title, n.japanOffices.lead)}
            <div className="kds-offices">
              {n.japanOffices.offices.map((off, i) => (
                <div className="kds-office-card" key={i}>
                  <p className="pin">{off.pin}</p>
                  <h3>{off.title}</h3>
                  <p className="addr">{off.addr}</p>
                  <p className="role">{off.role}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="kds-section kds-section-highlight">
          <div className="kds-container">
            {this.renderSectionHead(n.chinaHubs.kicker, n.chinaHubs.title, n.chinaHubs.lead)}
            <div className="kds-region-list">
              {n.chinaHubs.regions.map((r, i) => (
                <div className="kds-region-item" key={i}>
                  <h4>{r.en}</h4>
                  <p>{r.cn} — {r.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="kds-section">
          <div className="kds-container">
            {this.renderSectionHead(n.asia.kicker, n.asia.title, n.asia.lead)}
            <div className="kds-region-list">
              {n.asia.regions.map((r, i) => (
                <div className="kds-region-item" key={i}>
                  <h4>{r.en}</h4>
                  <p>{r.cn}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="kds-section kds-section-highlight">
          <div className="kds-container">
            {this.renderSectionHead(n.carriers.kicker, n.carriers.title, n.carriers.lead)}
            {n.carriers.groups.map((group, gi) => (
              <div className="kds-carrier-block" key={gi}>
                <p className="kds-carrier-cat">{group.cat}</p>
                <div className="kds-carrier-row">
                  {group.items.map((item, ci) => (
                    <div className="kds-carrier" key={ci}>
                      <strong>{item.en}</strong>
                      <span>{item.cn}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {this.renderCTA(n.cta)}
      </>
    );
  };

  // ========== TRACKING PAGE ==========

  renderTracking = () => {
    const d = this.importData();
    const t = d.tracking;
    const { trackInput, trackResult, trackNotFound, trackLoading } = this.state;
    return (
      <>
        <section className="kds-page-hero">
          <div className="kds-container">
            <div className="breadcrumb"><a href="#home" onClick={(e) => { e.preventDefault(); this.setPage('home'); }}>HOME</a> &nbsp;／&nbsp; TRACKING</div>
            <h1>{t.hero.title}</h1>
            <p className="kds-lead">{t.hero.lead}</p>
          </div>
          <span className="kds-page-hero-en">Tracking</span>
        </section>

        <section className="kds-section">
          <div className="kds-container">
            <div className="kds-tracking-form-card">
              <h2>{t.form.title}</h2>
              <p>{t.form.desc}</p>
              <form className="kds-tracking-form" onSubmit={this.handleTrack}>
                <input type="text" placeholder={t.form.placeholder} value={trackInput} onChange={e => this.setState({ trackInput: e.target.value })} />
                <button type="submit"><SearchOutlined /> {this.t('查询', '検索')}</button>
              </form>
              <p className="kds-tracking-hint">{t.form.hint}</p>
            </div>

            {trackLoading && (
              <div className="kds-tracking-empty">{this.t('查询中...', '検索中...')}</div>
            )}

            {trackNotFound && (
              <div className="kds-tracking-empty">{t.empty}</div>
            )}

            {trackResult && (
              <div className="kds-tracking-result">
                <div className="kds-tracking-summary">
                  <div className="item">
                    <span>{this.t('追踪号', '追跡番号')}</span>
                    <strong>{trackResult.trackNo}</strong>
                  </div>
                  <div className="item">
                    <span>{this.t('当前状态', '現在ステータス')}</span>
                    <strong className="status">{trackResult.status}</strong>
                  </div>
                </div>
                <div className="kds-timeline">
                  {trackResult.events.map((ev, i) => (
                    <div className={`kds-timeline-item${i === 0 ? ' current' : ''}`} key={i}>
                      <div className="dot" />
                      <p className="time">{ev.time}</p>
                      <p className="event">{ev.event}</p>
                      {ev.loc && <p className="loc">{ev.loc}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="kds-section kds-section-highlight">
          <div className="kds-container">
            {this.renderSectionHead(t.faq.kicker, t.faq.title)}
            <table className="kds-spec-table">
              <tbody>
                {t.faq.rows.map((row, i) => (
                  <tr key={i}>
                    <th>{row.th}</th>
                    <td>{row.td}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </>
    );
  };

  // ========== CONTACT PAGE ==========

  renderContact = () => {
    const d = this.importData();
    const c = d.contact;
    const form = this.state.contactForm;
    return (
      <>
        <section className="kds-page-hero">
          <div className="kds-container">
            <div className="breadcrumb"><a href="#home" onClick={(e) => { e.preventDefault(); this.setPage('home'); }}>HOME</a> &nbsp;／&nbsp; CONTACT</div>
            <h1>{c.hero.title}</h1>
            <p className="kds-lead">{c.hero.lead}</p>
          </div>
          <span className="kds-page-hero-en">Contact</span>
        </section>

        <section className="kds-section">
          <div className="kds-container">
            <div className="kds-contact-grid">
              <div className="kds-contact-info">
                {this.renderSectionHead(c.info.kicker, c.info.title, c.info.desc)}
                <ul>
                  {c.info.items.map((item, i) => (
                    <li key={i}>
                      <strong>{item.key}</strong>
                      <span>{item.val}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="kds-contact-form">
                <form onSubmit={this.handleContactSubmit}>
                  <div className="row">
                    <div className="field">
                      <label>{c.form.fields.name}<span className="req">*</span></label>
                      <input type="text" placeholder={c.form.placeholders.name} value={form.name} onChange={e => this.handleContactChange('name', e.target.value)} required />
                    </div>
                    <div className="field">
                      <label>{c.form.fields.company}</label>
                      <input type="text" placeholder={c.form.placeholders.company} value={form.company} onChange={e => this.handleContactChange('company', e.target.value)} />
                    </div>
                  </div>
                  <div className="row">
                    <div className="field">
                      <label>{c.form.fields.email}<span className="req">*</span></label>
                      <input type="email" placeholder={c.form.placeholders.email} value={form.email} onChange={e => this.handleContactChange('email', e.target.value)} required />
                    </div>
                    <div className="field">
                      <label>{c.form.fields.phone}</label>
                      <input type="tel" placeholder={c.form.placeholders.phone} value={form.phone} onChange={e => this.handleContactChange('phone', e.target.value)} />
                    </div>
                  </div>
                  <div className="field">
                    <label>{c.form.fields.category}</label>
                    <select value={form.category} onChange={e => this.handleContactChange('category', e.target.value)}>
                      {c.form.categories.map((cat, i) => <option key={i} value={i === 0 ? '' : cat}>{cat}</option>)}
                    </select>
                  </div>
                  <div className="field">
                    <label>{c.form.fields.message}<span className="req">*</span></label>
                    <textarea placeholder={c.form.placeholders.message} value={form.message} onChange={e => this.handleContactChange('message', e.target.value)} required />
                  </div>
                  <div className="submit">
                    <button type="submit" className="kds-btn kds-btn-primary">{c.form.submit}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  };

  renderPage = () => {
    const { page } = this.state;
    switch (page) {
      case 'about': return this.renderAbout();
      case 'services': return this.renderServices();
      case 'network': return this.renderNetwork();
      case 'tracking': return this.renderTracking();
      case 'contact': return this.renderContact();
      default: return this.renderHome();
    }
  };

  render() {
    const { lang } = this.state;
    return (
      <div className={`kds-site kds-lang-${lang}`}>
        {this.renderHeader()}
        <main>
          {this.renderPage()}
        </main>
        {this.renderFooter()}
      </div>
    );
  }
}

export default Home;